import { EventEmitter, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "ngx-cookie-service";
import { SOLDO_COOKIE_OPTIONS } from "./soldo-cookie.service";

export type AcceptedLanguage = 'it' | 'en' | 'nl' | 'fr';

export type LanguageResolutionSource = 'cookie' | 'browser' | 'fallback';

export interface LanguageSelectionControlEvent {
    selectionAllowed: boolean
};

@Injectable()
export class I18nService {
    public static readonly COOKIE_NAME = 'registration-lang';

    public readonly languageSelectionControlEmitter: EventEmitter<LanguageSelectionControlEvent> = new EventEmitter();

    private _lang: AcceptedLanguage = 'en';
    private readonly _languageResolvedFrom: LanguageResolutionSource;

    constructor(
        private readonly translate: TranslateService,
        private readonly cookieService: CookieService
    ) {

        this.translate.setFallbackLang("en");

        let lang: AcceptedLanguage;
        let resolvedFrom: LanguageResolutionSource;
        if (this.cookieService.check(I18nService.COOKIE_NAME)) {
            lang = this.cookieService.get(I18nService.COOKIE_NAME) as AcceptedLanguage;
            resolvedFrom = 'cookie';
        } else {
            lang = this.translate.getBrowserLang().substring(0, 2) as AcceptedLanguage;
            resolvedFrom = 'browser';
        }

        if (this.getValidLanguages().includes(lang)) {
            this.switchTo(lang);
        } else {
            resolvedFrom = 'fallback';
            this.switchTo("en");
        }
        this._languageResolvedFrom = resolvedFrom;
    }

    getValidLanguages(): string[] {
        return ['en', 'it', 'nl', 'fr'];
    }

    switchTo(lang: AcceptedLanguage) {
        this._lang = lang;
        this.translate.use(lang);
        document.documentElement.lang = lang;
        this.cookieService.set(I18nService.COOKIE_NAME, lang, SOLDO_COOKIE_OPTIONS);
    }

    get lang(): AcceptedLanguage {
        return this._lang;
    }

    get languageResolvedFrom(): LanguageResolutionSource {
        return this._languageResolvedFrom;
    }

    languagetToCountry(lang: AcceptedLanguage): string {
        const langToISO = {
            it: "ITA",
            en: "GBR",
            fr: "FRA",
            nl: "NLD"
        };
        const country = langToISO[lang];
        return country || "ROE";
    }

    turnOffLanguageSelection(): void {
        this.languageSelectionControlEmitter.emit({
            selectionAllowed: false
        });
    }

    turnOnLanguageSelection(): void {
        this.languageSelectionControlEmitter.emit({
            selectionAllowed: true
        });
    }
}
